import { Typography } from "antd";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { TableRow as DetailsRow } from "@/views/DataDiscrepancies/Components";
import { linkRenderer, renderCell } from "@/utils/tableConfigs/GlobalFunctions";
import SeverityCell from "./SeverityCell";
import type { BreachTableRow } from "./utils";

const { Title } = Typography;

type BreachesDrawerProps = {
  selectedRecord: BreachTableRow | null;
  setSelectedRecord: (record: BreachTableRow | null) => void;
};

export default function BreachesDrawer({
  selectedRecord,
  setSelectedRecord,
}: BreachesDrawerProps) {
  const headersArray = [
    {
      key: "BREACH ID",
      value: (
        <span className="text-white">{selectedRecord?.breach_id || "-"}</span>
      ),
    },
  ];

  return (
    <ResizableDrawer
      headerTitle="Issues Information"
      headersArray={headersArray}
      sizeToggle={{
        collapsedWidth: 654,
        expandedWidth: 1250,
        initialWidth: 654,
      }}
      placement="right"
      open={selectedRecord !== null}
      className="data-management-drawer"
      onClose={() => setSelectedRecord(null)}
      closeIcon={null}
      closable={false}
    >
      {selectedRecord ? (
        <div className="flex flex-col gap-6">
          <section>
            <Title level={5}>Signal and Risk Context</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Signal ID"
                    value={renderCell(
                      selectedRecord.signal_id,
                      linkRenderer((id) => `../signals?openSignal=${id}`),
                    )}
                  />
                  <DetailsRow
                    label="Signal"
                    value={renderCell(selectedRecord.signal)}
                  />
                  <DetailsRow
                    label="Linked Risks"
                    value={renderCell(
                      selectedRecord.risks,
                      linkRenderer((id) => `../risks?openRisk=${id}`),
                    )}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Breach Assessment</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Detection Date"
                    value={renderCell(selectedRecord.detection_date)}
                  />
                  <DetailsRow
                    label="Signal Value"
                    value={renderCell(selectedRecord.signal_value)}
                  />
                  <DetailsRow
                    label="Threshold Breached"
                    value={renderCell(selectedRecord.threshold_breached)}
                  />
                  <DetailsRow
                    label="Severity"
                    value={<SeverityCell value={selectedRecord.severity} />}
                  />
                  <DetailsRow
                    label="Status"
                    value={renderCell(selectedRecord.status)}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Response and Ownership</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Assigned To"
                    value={
                      <AvatarWithInitials
                        name={
                          typeof selectedRecord.assigned_to === "string" &&
                          selectedRecord.assigned_to.trim().length > 0
                            ? selectedRecord.assigned_to
                            : null
                        }
                        avatarSize={28}
                        labelFontSize={14}
                      />
                    }
                  />
                  <DetailsRow
                    label="Root Cause"
                    value={renderCell(selectedRecord.root_cause)}
                  />
                  <DetailsRow
                    label="Immediate Action"
                    value={renderCell(selectedRecord.immediate_action)}
                  />
                  <DetailsRow
                    label="CAPA (If Required)"
                    value={renderCell(selectedRecord.capa_if_required)}
                  />
                  <DetailsRow
                    label="Escalation Level"
                    value={renderCell(selectedRecord.escalation_level)}
                  />
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <Title level={5}>Review and Comments</Title>
            <div className="rounded-sm bg-white">
              <table className="w-full divide-y divide-solid divide-[#F0F0F0] px-4">
                <tbody className="divide-y divide-solid divide-[#F0F0F0]">
                  <DetailsRow
                    label="Next Review Date"
                    value={renderCell(selectedRecord.next_review_date)}
                  />
                  <DetailsRow
                    label="Comments"
                    value={renderCell(selectedRecord.comments)}
                  />
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : null}
    </ResizableDrawer>
  );
}
